/* eslint-disable @typescript-eslint/no-explicit-any */
import { notifications } from '@mantine/notifications';
import { fetchEventSource, type FetchEventSourceInit } from '@microsoft/fetch-event-source';

import { tokenManager } from './token-manager';

export type SortOrder = 'asc' | 'desc';

export interface PaginationOptions {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: SortOrder;
}

/**
 * Standard pagination metadata interface
 */
export interface PaginationMetadata {
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Standard paginated items response
 * Used for all collection endpoints
 */
export type PaginatedItems<T, E = {}> = {
  items: T[];
  pagination: PaginationMetadata;
} & E;

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: {
    message: string;
    code?: string;
    details?: Record<string, unknown>;
  };
  meta?: PaginationMetadata;
}

// Base fetch client configuration
type FetchOptions = RequestInit & {
  params?: Record<string, unknown>;
  requiresAuth?: boolean;
  _retry?: boolean;
};

// Fetch client with interceptors
class FetchClient {
  private baseUrl = new URL('/api/', window.location.origin);

  private async doRequest(endpoint: string, options: FetchOptions = {}): Promise<Response> {
    const { params, requiresAuth = true, ...fetchOptions } = options;

    // Set default headers
    const headers = new Headers(fetchOptions.headers || {});

    const isFormData = options.body instanceof FormData;
    if (!headers.has('Content-Type') && options.body && !isFormData) {
      headers.set('Content-Type', 'application/json');
    }

    if (isFormData && headers.has('Content-Type')) {
      headers.delete('Content-Type');
    }

    // Add authorization header if needed
    if (requiresAuth) {
      try {
        const token = await tokenManager.getTokenForRequest();
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        }
      } catch {
        // Continue without token
      }
    }

    // Process URL parameters using URL API
    const url = new URL(endpoint.replace(/^\//, ''), this.baseUrl);

    if (params && Object.keys(params).length > 0) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, String(value));
      });
    }

    // Perform the fetch operation
    return fetch(url, {
      ...fetchOptions,
      headers,
      credentials: 'include' as RequestCredentials, // Include cookies
    });
  }

  private async processResponse<T>(response: Response): Promise<T> {
    // Parse JSON response
    const contentType = response.headers.get('Content-Type');
    let responseData: string | ApiResponse<T>;

    if (contentType?.includes('application/json')) {
      responseData = await response.json();
    } else {
      responseData = await response.text();
    }

    // Handle errors based on status code
    if (!response.ok) {
      throw this.createErrorFromResponse(response, responseData);
    }

    // Handle standardized API response format
    if (typeof responseData === 'object' && responseData !== null && 'success' in responseData) {
      if (responseData.success === true && 'data' in responseData) {
        // Create pagination if available
        if (responseData.meta && responseData.data && typeof responseData.data === 'object') {
          return {
            ...responseData.data,
            _pagination: {
              totalCount: responseData.meta.totalCount || 0,
              currentPage: responseData.meta.currentPage || 0,
              pageSize: responseData.meta.pageSize || 10,
              totalPages: responseData.meta.totalPages || 1,
            } as PaginationMetadata,
          };
        }
        return responseData.data as T;
      } else if (responseData.success === false) {
        throw this.createErrorFromApiResponse(responseData);
      }
    }

    return responseData as T;
  }

  private createErrorFromResponse(response: Response, data: any): Error {
    // Create standardized error format from response
    const status = response.status;
    let message = `HTTP error ${status}`;
    let code: string | undefined;
    let details: Record<string, unknown> | undefined;

    // Try to extract error details from response
    if (data && typeof data === 'object') {
      if (data.error) {
        message = data.error.message || message;
        code = data.error.code;
        details = data.error.details;
      } else if (data.message) {
        message = data.message;
      }
    }

    const error = new Error(message) as Error & {
      status?: number;
      code?: string;
      details?: Record<string, unknown>;
    };

    error.status = status;
    if (code) error.code = code;
    if (details) error.details = details;

    return error;
  }

  private createErrorFromApiResponse(data: ApiResponse): Error {
    const message = data.error?.message || 'Unknown API error';
    const error = new Error(message) as Error & {
      code?: string;
      details?: Record<string, unknown>;
    };

    if (data.error?.code) error.code = data.error.code;
    if (data.error?.details) error.details = data.error.details;

    return error;
  }

  private async handleRetry<T>(endpoint: string, options: FetchOptions, originalError: any): Promise<T> {
    // Only retry on auth errors (401) for requests that require auth
    if (originalError.status === 401 && !options._retry && options.requiresAuth) {
      // Mark as retry attempt
      options._retry = true;

      try {
        // Try to refresh token
        const newToken = await tokenManager.refreshToken();

        if (newToken) {
          // Retry the original request with new token
          return await this.request<T>(endpoint, options);
        }
      } catch {
        // If refresh fails, throw original error
      }
    }
    throw originalError;
  }

  async request<T = any>(endpoint: string, options: FetchOptions = {}): Promise<T> {
    const normalizedOptions: FetchOptions = {
      ...options,
      requiresAuth: options.requiresAuth ?? true,
    };

    try {
      const response = await this.doRequest(endpoint, normalizedOptions);
      return await this.processResponse<T>(response);
    } catch (error: unknown) {
      // Handle auth retry logic
      if (error instanceof Error && 'status' in error && error.status === 401 && !normalizedOptions._retry) {
        return this.handleRetry<T>(endpoint, normalizedOptions, error);
      }
      throw error;
    }
  }

  // Convenience methods
  async get<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  // Get method with pagination support
  async getPaginated<T, E = {}>(
    endpoint: string,
    paginationOptions: PaginationOptions = {},
    options: FetchOptions = {}
  ): Promise<PaginatedItems<T, E>> {
    const { _pagination, ...rest } = await this.request<{ items: T[]; _pagination: PaginationMetadata } & E>(endpoint, {
      ...options,
      method: 'GET',
      params: removeUndefined({ ...options.params, ...paginationOptions }),
    });

    // Extract pagination from _pagination
    const pagination: PaginationMetadata = _pagination || {
      totalCount: 0,
      currentPage: 0,
      pageSize: 10,
      totalPages: 0,
    };

    return { pagination, ...rest } as PaginatedItems<T, E>;
  }

  async post<T = any>(endpoint: string, data?: any, options: FetchOptions = {}): Promise<T> {
    // Special handling for FormData
    if (data instanceof FormData) {
      // For FormData, don't set any Content-Type header
      const formDataOptions: FetchOptions = {
        ...options,
        method: 'POST',
        body: data,
        headers: {
          ...(options.headers || {}),
        },
      };

      // Make sure no Content-Type is set
      if (formDataOptions.headers && 'Content-Type' in formDataOptions.headers) {
        delete formDataOptions.headers['Content-Type'];
      }

      return this.request<T>(endpoint, formDataOptions);
    }

    // Regular JSON handling
    const body = data !== undefined ? JSON.stringify(data) : undefined;
    return this.request<T>(endpoint, { ...options, method: 'POST', body });
  }

  async put<T = any>(endpoint: string, data?: any, options: FetchOptions = {}): Promise<T> {
    const body = data !== undefined ? JSON.stringify(data) : undefined;
    return this.request<T>(endpoint, { ...options, method: 'PUT', body });
  }

  async patch<T = any>(endpoint: string, data?: any, options: FetchOptions = {}): Promise<T> {
    const body = data !== undefined ? JSON.stringify(data) : undefined;
    return this.request<T>(endpoint, { ...options, method: 'PATCH', body });
  }

  async delete<T = any>(endpoint: string, options: FetchOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  async getBlob(endpoint: string, options: FetchOptions = {}): Promise<Blob> {
    const response = await this.doRequest(endpoint, { ...options, method: 'GET' });
    if (!response.ok) {
      throw this.createErrorFromResponse(response, await response.text());
    }
    return response.blob();
  }

  /**
   * Subscribe to a Server-Sent Events (SSE) endpoint
   * @param endpoint The SSE endpoint (relative to /api/)
   * @param options FetchEventSourceInit options (handlers)
   * @returns AbortController for cleanup
   */
  public async streamEvents(endpoint: string, options: FetchEventSourceInit): Promise<AbortController> {
    const url = new URL(endpoint.replace(/^\//, ''), this.baseUrl);
    const controller = new AbortController();

    fetchEventSource(url.toString(), {
      ...options,
      fetch: (_input, init) => {
        // Remove Content-Type for SSE
        const headers: Record<string, string> = { ...((init?.headers as Record<string, string>) || {}) };
        if ('Content-Type' in headers) {
          delete headers['Content-Type'];
        }
        return this.doRequest(endpoint, {
          ...init,
          headers,
          signal: controller.signal,
        });
      },
      signal: controller.signal,
    });

    return controller;
  }
}

// Create a single API client instance
export const apiClient = new FetchClient();

// Common error handling utility
export const handleApiError = (error: unknown, message: string): never => {
  // Extract error message from response if possible
  let errorMessage = message;

  if (error && typeof error === 'object') {
    if ('message' in error && typeof error.message === 'string') {
      errorMessage = error.message;
    }
  }

  // Show error notification
  notifications.show({
    title: 'Error',
    message: errorMessage,
    color: 'red',
    autoClose: 5000,
  });

  throw new Error(errorMessage);
};

function removeUndefined<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== undefined)) as Partial<T>;
}
